import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { CanCreate, showWhenPermission } from '@/shared/components/security';
import PropTypes from 'prop-types';
import api from '@/shared/api';
import intlShape from '@/shared/utils/intlShape';
import config from '@/config';
import { dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import { AddIcon } from '@/shared/components/Icons';
import YesNoModal from '@/shared/components/YesNoModal';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import messages from './priceGridMessages';
import PriceGridNameModal from './PriceGridNameModal';
import LoadingAnimation from '../../shared/components/LoadingAnimation';
import PriceGrid from './PriceGrid';
import PriceGridSideBar from './PriceGridSideBar';

const gridSort = dynamicSortMultiple('-isDefault', 'name');

class PriceGridContainer extends Component {
  state = {
    grids: null,
    activeGrid: null,
    editGrid: null,
    deleteGrid: null,
  };

  componentDidMount() {
    const { intl } = this.props;
    api.base.get(`${config.appUrls.api}/api/Price/Grid`).then(({ data }) => {
      const grids = [...data]
        .map(x => ({
          ...x,
          name: x.name === 'Default' ? intl.formatMessage(messages.Default) : x.name,
        }))
        .sort(gridSort);
      let activeGrid = grids.find(x => x.isDefault);
      if (!activeGrid && grids.length) [activeGrid] = grids;

      this.setState(state => ({
        ...state,
        grids,
        activeGrid: activeGrid.id,
      }));
    });
  }

  changeGrid = grid => {
    this.setState(state => ({
      ...state,
      activeGrid: grid.id,
    }));
  };
  createGrid = () => {
    const { intl } = this.props;
    this.setState({
      editGrid: {
        id: null,
        name: '',
        onSave: ({ name }) =>
          api.base.post(`${config.appUrls.api}/api/Price/CreateGrid`, { name }).then(({ data }) => {
            this.setState(state => ({
              grids: [...state.grids, data].sort(gridSort),
              activeGrid: data.id,
              editGrid: null,
            }));
            return data;
          }),
        title: intl.formatMessage(messages.CreatePriceGrid),
      },
    });
  };
  copyGrid = grid => {
    const { intl } = this.props;
    this.setState({
      editGrid: {
        ...grid,
        onSave: ({ id, name }) =>
          api.base
            .post(`${config.appUrls.api}/api/Price/CopyGrid`, { id, name })
            .then(({ data }) => {
              this.setState(state => ({
                grids: [...state.grids, data].sort(gridSort),
                activeGrid: data.id,
                editGrid: null,
              }));
              return data;
            }),
        title: intl.formatMessage(messages.CopyPriceGrid),
      },
    });
  };

  editGrid = grid => {
    const { intl } = this.props;
    this.setState({
      editGrid: {
        ...grid,
        onSave: ({ id, name }) =>
          api.base
            .put(`${config.appUrls.api}/api/Price/UpdateGrid`, { id, name })
            .then(({ data }) => {
              this.setState(state => ({
                grids: state.grids
                  .map(g => {
                    if (g.id === data.id) {
                      return { ...data };
                    }
                    return g;
                  })
                  .sort(gridSort),
                editGrid: null,
              }));
              return data;
            }),
        title: intl.formatMessage(messages.ChangePriceGridName),
      },
    });
  };
  deleteGrid = ({ id }) =>
    api.base.delete(`${config.appUrls.api}/api/Price/Delete/${id}`).then(() => {
      const { activeGrid, grids } = this.state;
      let aGrid = activeGrid;
      if (activeGrid.id === id) {
        aGrid = grids.find(x => x.isDefault);
        if (!aGrid && grids.length) [aGrid] = grids;
      }
      this.setState(state => ({
        grids: state.grids.filter(x => x.id !== id).sort(gridSort),
        activeGrid: aGrid,
        deleteGrid: null,
      }));
    });

  setDefaultGrid = ({ id }) =>
    api.base.put(`${config.appUrls.api}/api/Price/SetDefault/${id}`).then(({ data }) => {
      this.setState(({ grids }) => ({
        grids: grids.map(x => {
          const updatedGrid = data.find(g => g.id === x.id);
          if (!updatedGrid) return x;
          return { ...x, ...updatedGrid };
        }),
      }));
    });

  render() {
    const { grids, activeGrid, editGrid, deleteGrid } = this.state;
    const { intl } = this.props;

    return grids ? (
      <div>
        <div className="col-md-3 col-lg-2 sidebar">
          <CanCreate on="pricelist">
            <div className="text-right margin5B">
              <ZendeskHelp article="5434829034770" className="margin10R" />

              <button type="button" className="btn btn-default btn-sm" onClick={this.createGrid}>
                <AddIcon className="margin5R" />
                <span>{intl.formatMessage(messages.CreatePriceGrid)}</span>
              </button>
            </div>
          </CanCreate>
          <PriceGridSideBar
            grids={grids}
            activeGrid={activeGrid}
            onChangeGrid={this.changeGrid}
            onCopyGrid={this.copyGrid}
            onDeleteGrid={grid => this.setState({ deleteGrid: grid })}
            onEditGrid={this.editGrid}
            onSetDefault={this.setDefaultGrid}
          />
        </div>
        <div className="col-md-9 col-lg-10">{activeGrid && <PriceGrid gridId={activeGrid} />}</div>
        {editGrid && (
          <PriceGridNameModal
            grid={editGrid}
            title={editGrid.title}
            onSave={editGrid.onSave}
            onClose={() => this.setState({ editGrid: null })}
          />
        )}
        <YesNoModal
          show={!!deleteGrid}
          title={intl.formatMessage(messages.DeletePriceGrid)}
          message={intl.formatMessage(messages.DeletePriceGridConfirmationMsg)}
          onNo={() =>
            this.setState({
              deleteGrid: null,
            })
          }
          onSave={() => this.deleteGrid(deleteGrid)}
        />
      </div>
    ) : (
      <LoadingAnimation />
    );
  }
}

PriceGridContainer.propTypes = {
  intl: intlShape.isRequired,
};

export default showWhenPermission('pricelist', 'read')(injectIntl(PriceGridContainer));
